/** EMPLOYEE_ROLES control: show the operator's existing employee roles and which
 *  of the standard ones are still missing.
 *
 *  Creating them is the item's own run-style button in the action bar — this
 *  control used to carry a second button firing exactly the same
 *  `items.execute(topId, item.id)` from two feet away, which is the one thing a
 *  bespoke control must not do (see `bespokeExecute`).
 */
import { Box, Chip, Stack, Typography } from '@mui/material';

import type { PreflightItem } from '@/api/preflight';

function parseNames(raw: unknown): string[] {
  return Array.isArray(raw) ? raw.filter((x): x is string => typeof x === 'string') : [];
}

export function EmployeeRolesControl({ item }: { item: PreflightItem }) {
  const existing = parseNames(item.detail.existing_roles);
  const standard = parseNames(item.detail.standard_roles);
  const missing = standard.filter((s) => !existing.includes(s));

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack spacing={1}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            Roles:
          </Typography>
          {existing.length ? (
            existing.map((r) => <Chip key={r} size="small" label={r} variant="outlined" />)
          ) : (
            <Typography variant="body2" color="text.secondary">
              none yet
            </Typography>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary">
          {missing.length
            ? `Missing standard roles: ${missing.join(', ')} — Run creates them.`
            : 'Every standard role exists. Run is idempotent; it re-checks them.'}
        </Typography>
      </Stack>
    </Box>
  );
}
